import React from 'react';

import { useQuery } from '@tanstack/react-query';
import * as LDAP from 'src/config/LDAP';
import { getAccommodationQueryOptions } from 'src/services/accommodation/accommodationQueries';
import { type ActivePlacementMetricsResponse } from 'src/services/accommodation/types';
import { useAppSelector } from 'src/store/store';
import { formatPercent } from 'src/utils/numbersUtils';

import PlacementMetricCard, { type PlacementMetricCardProps } from './placementMetricCard';
import PlacementMetricsBand from './placementMetricsBand';

type TierMetric = Exclude<keyof ActivePlacementMetricsResponse, 'totalPlacements' | 'unclassifiedPlacements'>;

const TIER_CARDS: ReadonlyArray<{ label: string; metric: TierMetric }> = [
  { label: 'Process Path (Unmodified)', metric: 'processPathUnmodified' },
  { label: 'Process Path (Modified)', metric: 'processPathModified' },
  { label: 'Temporary Light Duty', metric: 'temporaryLightDuty' },
  { label: 'Multi-Job Type', metric: 'multiJobTypes' },
];

/** Builds the "56% of total" line. The total includes unclassified placements, so tiers may not add up to 100%. */
const formatShareOfTotal = (count: number, total: number) => {
  const percent = formatPercent(count, total);
  return percent === null ? 'N/A' : `${percent} of total`;
};

/** Gates and fetches the Approved Active Placements band for the selected site and source. */
const PlacementMetricsBandContainer = () => {
  const { ldap, featureFlags, userLoading } = useAppSelector((state) => state.user);
  const { selectedSite } = useAppSelector((state) => state.sites);
  const { placementType } = useAppSelector((state) => state.placementOverviewPage.globalFilterOptions);

  const isAdmin = ldap.includes(LDAP.ADMIN);
  // Admins see the band even when the flag is off
  const canViewPlacementMetrics = isAdmin || featureFlags.PLACEMENT_OVERVIEW_METRICS;
  const hasSelectedSite = selectedSite.id > 0;
  // A null placementType means All sources, which omits accommodationType
  const accommodationType = placementType ?? undefined;

  const metricsQuery = useQuery({
    ...getAccommodationQueryOptions.activePlacementMetrics(selectedSite.id, accommodationType),
    enabled: userLoading === 'fulfilled' && canViewPlacementMetrics && hasSelectedSite,
    // Keep the previous numbers across Source switches only, never across sites (queryKey[1] is siteId)
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === selectedSite.id ? previousData : undefined,
  });

  if (!canViewPlacementMetrics) {
    return null;
  }

  const metrics = metricsQuery.data;
  // Total only gets a second line for the admin note. null keeps it empty instead of showing "—" while loading
  const unclassifiedNote =
    isAdmin && metrics !== undefined && metrics.unclassifiedPlacements > 0
      ? `Includes ${metrics.unclassifiedPlacements} unclassified`
      : null;
  const cards: PlacementMetricCardProps[] = [
    { label: 'Total Placements', count: metrics?.totalPlacements, subheader: unclassifiedNote },
    ...TIER_CARDS.map(({ label, metric }) => ({
      label,
      count: metrics?.[metric],
      subheader: metrics === undefined ? undefined : formatShareOfTotal(metrics[metric], metrics.totalPlacements),
    })),
  ];
  // The band shows this instead of the cards, so cached numbers never sit next to a failure
  const loadError = metricsQuery.isError
    ? {
        message: `Placement metrics could not be loaded for ${selectedSite.site}.`,
        onRetry: () => metricsQuery.refetch(),
      }
    : undefined;
  // Placeholder data has no fetch time of its own and can show without a fetch while offline
  const lastFetchedAt = metricsQuery.isSuccess && !metricsQuery.isPlaceholderData ? metricsQuery.dataUpdatedAt : null;

  return (
    <PlacementMetricsBand isFetching={metricsQuery.isFetching} loadError={loadError} lastFetchedAt={lastFetchedAt}>
      {cards.map((cardProps) => (
        <PlacementMetricCard key={cardProps.label} {...cardProps} />
      ))}
    </PlacementMetricsBand>
  );
};

export default PlacementMetricsBandContainer;
